import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Radio } from "lucide-react";
import { LogoMark } from "./doodles";
import { scrollToSection } from "@/config/station";

const LINKS = [
    { id: "listen-live", label: "Listen Live" },
    { id: "snooze-button", label: "The Show" },
    { id: "latest-episodes", label: "Episodes" },
    { id: "stay-connected", label: "Stay Connected" },
    { id: "show-love", label: "Show Some Love" },
    { id: "submit-music", label: "Submit Music" },
    { id: "about", label: "About" },
];

export default function Nav() {
    const [open, setOpen] = useState(false);

    const go = (id) => {
        setOpen(false);
        scrollToSection(id);
    };

    return (
        <header
            data-testid="main-navigation-bar"
            className="sticky top-0 z-50 border-b-[2.5px] border-ink bg-cream/95 backdrop-blur-md"
        >
            <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
                <button
                    data-testid="nav-brand-button"
                    onClick={() => go("home")}
                    className="flex items-center gap-2.5 text-left focus:outline-none focus-visible:ring-4 focus-visible:ring-sunny/70 rounded-lg"
                >
                    <LogoMark className="h-10 w-10 shrink-0" />
                    <span className="leading-none">
                        <span className="block font-mono2 text-[10px] font-bold uppercase tracking-[0.2em] text-ink2">
                            MyRadio1st Presents
                        </span>
                        <span className="block font-marker text-lg text-ink">Inspiration Station</span>
                    </span>
                </button>

                <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary">
                    {LINKS.map((l) => (
                        <button
                            key={l.id}
                            data-testid={`nav-link-${l.id}`}
                            onClick={() => go(l.id)}
                            className="rounded-lg px-3 py-2 font-condensed text-base font-bold uppercase tracking-wide text-ink transition-colors hover:bg-creamalt focus:outline-none focus-visible:ring-4 focus-visible:ring-sunny/70"
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        data-testid="nav-tune-in-button"
                        onClick={() => go("listen-live")}
                        className="btn-ink ml-3 !px-5 !py-2 bg-sunny hover:bg-sunnyhover text-base"
                    >
                        <Radio className="h-4 w-4" strokeWidth={2.75} />
                        Tune In
                    </button>
                </nav>

                <button
                    data-testid="mobile-menu-button"
                    onClick={() => setOpen((v) => !v)}
                    aria-expanded={open}
                    aria-label={open ? "Close menu" : "Open menu"}
                    className="rounded-lg border-[2.5px] border-ink bg-white p-2 shadow-[3px_3px_0_#1A1918] lg:hidden"
                >
                    {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.nav
                        data-testid="mobile-menu-panel"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.25, ease: "easeOut" }}
                        className="overflow-hidden border-t-[2.5px] border-ink bg-cream lg:hidden"
                        aria-label="Mobile"
                    >
                        <div className="flex flex-col gap-1 px-4 py-4">
                            {LINKS.map((l) => (
                                <button
                                    key={l.id}
                                    data-testid={`mobile-nav-link-${l.id}`}
                                    onClick={() => go(l.id)}
                                    className="rounded-lg px-3 py-3 text-left font-condensed text-xl font-bold uppercase tracking-wide text-ink hover:bg-creamalt"
                                >
                                    {l.label}
                                </button>
                            ))}
                            <button
                                data-testid="mobile-nav-tune-in-button"
                                onClick={() => go("listen-live")}
                                className="btn-ink mt-2 bg-sunny hover:bg-sunnyhover"
                            >
                                <Radio className="h-4 w-4" strokeWidth={2.75} />
                                Tune In
                            </button>
                        </div>
                    </motion.nav>
                )}
            </AnimatePresence>
        </header>
    );
}
